import React, { useState, useEffect, useMemo } from 'react';
import { QUESTION_TOPICS } from '../../types';
import type { Question, QuestionTopic, QuestionDifficulty } from '../../types';
import { questionService } from '../../services/questionBankService';
import { validateQuestions, questionBankStats, type ValidationError } from '../../utils/validateQuestions';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '@flowcore-water/ui';

type ViewMode = 'list' | 'edit';

const DIFFICULTIES: { id: QuestionDifficulty; label: string }[] = [
  { id: 'basic', label: 'Basic' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

const emptyQuestion = (id: string): Question => ({
  id,
  topic: 'field-procedures',
  difficulty: 'basic',
  question: '',
  choices: [
    { id: 'a', text: '' },
    { id: 'b', text: '' },
    { id: 'c', text: '' },
    { id: 'd', text: '' },
  ],
  correctAnswer: 'a',
  explanation: '',
  isMath: false,
  linkedQualIds: [],
});

export const QuestionEditor: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTheme();
  const isReadOnly = user?.role === 'outside_observer';

  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [saveErrors, setSaveErrors] = useState<ValidationError[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTopic, setFilterTopic] = useState<QuestionTopic | ''>('');
  const [filterDifficulty, setFilterDifficulty] = useState<QuestionDifficulty | ''>('');
  const [bulkErrors, setBulkErrors] = useState<ValidationError[]>([]);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    const loaded = await questionService.getAll();
    setQuestions(loaded);
    setLoading(false);
  };

  const stats = useMemo(() => questionBankStats(questions), [questions]);

  const filteredQuestions = useMemo(() => {
    return questions.filter((q) => {
      if (filterTopic && q.topic !== filterTopic) return false;
      if (filterDifficulty && q.difficulty !== filterDifficulty) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          q.id.toLowerCase().includes(query) ||
          q.question.toLowerCase().includes(query) ||
          q.explanation.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [questions, filterTopic, filterDifficulty, searchQuery]);

  const handleNew = () => {
    const nextId = questionService.nextId(questions);
    setEditingQuestion(emptyQuestion(nextId));
    setSaveErrors([]);
    setViewMode('edit');
  };

  const handleEdit = (q: Question) => {
    setEditingQuestion({ ...q, choices: q.choices.map((c) => ({ ...c })) });
    setSaveErrors([]);
    setViewMode('edit');
  };

  const handleDelete = async (questionId: string) => {
    if (!confirm(`Delete question ${questionId}? This cannot be undone.`)) return;
    await questionService.remove(questionId, user!.uid);
    setQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  const handleSave = async () => {
    if (!editingQuestion) return;
    const errors = await questionService.save(editingQuestion);
    if (errors.length) {
      setSaveErrors(errors);
      return;
    }
    // Update local state
    setQuestions((prev) => {
      const idx = prev.findIndex((q) => q.id === editingQuestion.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = editingQuestion;
        return updated;
      }
      return [...prev, editingQuestion];
    });
    setViewMode('list');
    setEditingQuestion(null);
    setSaveErrors([]);
  };

  const handleValidateAll = () => {
    const errors = validateQuestions(questions);
    setBulkErrors(errors);
  };

  const handleDuplicate = (q: Question) => {
    const nextId = questionService.nextId(questions);
    setEditingQuestion({
      ...q,
      id: nextId,
      choices: q.choices.map((c) => ({ ...c })),
    });
    setSaveErrors([]);
    setViewMode('edit');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p style={{ color: t.textSecondary }}>Loading question bank...</p>
      </div>
    );
  }

  // ── Edit View ──
  if (viewMode === 'edit' && editingQuestion) {
    return (
      <QuestionForm
        question={editingQuestion}
        onChange={setEditingQuestion}
        onSave={handleSave}
        onCancel={() => { setViewMode('list'); setEditingQuestion(null); setSaveErrors([]); }}
        errors={saveErrors}
      />
    );
  }

  // ── List View ──
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: t.textPrimary }}>
            Question Bank Editor
          </h2>
          <p className="text-sm mt-1" style={{ color: t.textSecondary }}>
            {stats.total} questions — {stats.byDifficulty['basic'] || 0} basic, {stats.byDifficulty['intermediate'] || 0} intermediate, {stats.byDifficulty['advanced'] || 0} advanced
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleValidateAll}
            className="px-4 py-2 rounded text-sm font-medium transition-colors"
            style={{ background: t.surfaceHover, color: t.textSecondary }}
          >
            Validate All
          </button>
          {!isReadOnly && (
            <button
              onClick={handleNew}
              className="px-4 py-2 rounded text-sm font-medium transition-colors"
              style={{ background: t.accent, color: '#fff' }}
            >
              + New Question
            </button>
          )}
        </div>
      </div>

      {/* Validation results */}
      {bulkErrors.length > 0 && (
        <div className="rounded-lg border p-4 space-y-1" style={{ borderColor: '#b91c1c', background: 'rgba(185,28,28,0.1)' }}>
          <p className="font-semibold text-sm" style={{ color: '#fca5a5' }}>
            {bulkErrors.length} validation error(s) found:
          </p>
          {bulkErrors.slice(0, 20).map((e, i) => (
            <p key={i} className="text-xs" style={{ color: '#fca5a5' }}>
              [{e.questionId}] {e.field}: {e.message}
            </p>
          ))}
          {bulkErrors.length > 20 && (
            <p className="text-xs" style={{ color: '#fca5a5' }}>
              ...and {bulkErrors.length - 20} more
            </p>
          )}
        </div>
      )}
      {bulkErrors.length === 0 && bulkErrors !== undefined && questions.length > 0 && (
        <div className="rounded-lg border p-3" style={{ borderColor: '#16a34a', background: 'rgba(22,163,74,0.1)' }}>
          <p className="text-sm font-medium" style={{ color: '#86efac' }}>
            All {questions.length} questions pass validation.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search questions..."
          className="px-3 py-2 rounded text-sm flex-1 min-w-[200px]"
          style={{ background: t.surfaceHover, color: t.textPrimary, border: `1px solid ${t.border}` }}
        />
        <select
          value={filterTopic}
          onChange={(e) => setFilterTopic(e.target.value as QuestionTopic | '')}
          className="px-3 py-2 rounded text-sm"
          style={{ background: t.surfaceHover, color: t.textPrimary, border: `1px solid ${t.border}` }}
        >
          <option value="">All Topics</option>
          {QUESTION_TOPICS.map((tp) => (
            <option key={tp.id} value={tp.id}>{tp.label}</option>
          ))}
        </select>
        <select
          value={filterDifficulty}
          onChange={(e) => setFilterDifficulty(e.target.value as QuestionDifficulty | '')}
          className="px-3 py-2 rounded text-sm"
          style={{ background: t.surfaceHover, color: t.textPrimary, border: `1px solid ${t.border}` }}
        >
          <option value="">All Difficulties</option>
          {DIFFICULTIES.map((d) => (
            <option key={d.id} value={d.id}>{d.label}</option>
          ))}
        </select>
      </div>

      <p className="text-sm" style={{ color: t.textSecondary }}>
        Showing {filteredQuestions.length} of {questions.length}
      </p>

      {/* Question list */}
      <div className="space-y-2">
        {filteredQuestions.map((q) => (
          <QuestionRow
            key={q.id}
            question={q}
            onEdit={() => handleEdit(q)}
            onDuplicate={() => handleDuplicate(q)}
            onDelete={() => handleDelete(q.id)}
            isReadOnly={isReadOnly}
          />
        ))}
        {filteredQuestions.length === 0 && (
          <p className="text-center py-8" style={{ color: t.textSecondary }}>
            No questions match your filters.
          </p>
        )}
      </div>
    </div>
  );
};

// ── Row Component ──

const QuestionRow: React.FC<{
  question: Question;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isReadOnly: boolean;
}> = ({ question: q, onEdit, onDuplicate, onDelete, isReadOnly }) => {
  const { t } = useTheme();

  const diffColor = q.difficulty === 'basic' ? '#22c55e'
    : q.difficulty === 'intermediate' ? '#eab308'
    : '#ef4444';

  return (
    <div
      className="rounded-lg border p-4 flex items-start gap-4 transition-colors"
      style={{ background: t.cardBg, borderColor: t.border }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: t.surfaceHover, color: t.textSecondary }}>
            {q.id}
          </span>
          <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: `${diffColor}20`, color: diffColor }}>
            {q.difficulty}
          </span>
          <span className="text-xs px-2 py-0.5 rounded" style={{ background: `${t.accent}20`, color: t.accent }}>
            {QUESTION_TOPICS.find((tp) => tp.id === q.topic)?.label || q.topic}
          </span>
          {q.isMath && (
            <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(168,85,247,0.2)', color: '#a855f7' }}>
              Math
            </span>
          )}
        </div>
        <p className="text-sm truncate" style={{ color: t.textPrimary }}>
          {q.question}
        </p>
        <p className="text-xs mt-1" style={{ color: t.textSecondary }}>
          Answer: {q.correctAnswer.toUpperCase()} — {q.linkedQualIds.join(', ')}
        </p>
      </div>
      {!isReadOnly && (
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit} className="px-3 py-1.5 rounded text-xs font-medium transition-colors" style={{ background: t.surfaceHover, color: t.textSecondary }}>
            Edit
          </button>
          <button onClick={onDuplicate} className="px-3 py-1.5 rounded text-xs font-medium transition-colors" style={{ background: t.surfaceHover, color: t.textSecondary }}>
            Dup
          </button>
          <button onClick={onDelete} className="px-3 py-1.5 rounded text-xs font-medium transition-colors hover:bg-red-900/30" style={{ color: '#ef4444' }}>
            Del
          </button>
        </div>
      )}
    </div>
  );
};

// ── Form Component ──

const QuestionForm: React.FC<{
  question: Question;
  onChange: (q: Question) => void;
  onSave: () => void;
  onCancel: () => void;
  errors: ValidationError[];
}> = ({ question: q, onChange, onSave, onCancel, errors }) => {
  const { t } = useTheme();

  const update = <K extends keyof Question>(field: K, value: Question[K]) =>
    onChange({ ...q, [field]: value });

  const updateChoice = (choiceId: string, text: string) => {
    onChange({
      ...q,
      choices: q.choices.map((c) => (c.id === choiceId ? { ...c, text } : c)),
    });
  };

  const toggleQualId = (qualId: string) => {
    const current = q.linkedQualIds;
    if (current.includes(qualId)) {
      update('linkedQualIds', current.filter((id) => id !== qualId));
    } else {
      update('linkedQualIds', [...current, qualId]);
    }
  };

  const QUAL_OPTIONS = [
    { id: 'baq', label: 'BAQ' },
    { id: 'electrical', label: 'Electrical' },
    { id: 'hoist', label: 'Hoist' },
    { id: 'bighoss', label: 'Big Hoss' },
    { id: 'lead_install', label: 'Lead Install' },
    { id: 'lead_diagnostic', label: 'Lead Diagnostic' },
  ];

  const inputStyle = {
    background: t.surfaceHover,
    color: t.textPrimary,
    border: `1px solid ${t.border}`,
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold" style={{ color: t.textPrimary }}>
          {q.id.startsWith('q') && parseInt(q.id.slice(1)) > 0 ? `Edit ${q.id}` : 'New Question'}
        </h2>
        <button onClick={onCancel} className="text-sm px-3 py-1.5 rounded" style={{ background: t.surfaceHover, color: t.textSecondary }}>
          Cancel
        </button>
      </div>

      {errors.length > 0 && (
        <div className="rounded-lg border p-3 space-y-1" style={{ borderColor: '#b91c1c', background: 'rgba(185,28,28,0.1)' }}>
          {errors.map((e, i) => (
            <p key={i} className="text-sm" style={{ color: '#fca5a5' }}>
              {e.field}: {e.message}
            </p>
          ))}
        </div>
      )}

      {/* ID (read only) */}
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: t.textSecondary }}>Question ID</label>
        <input type="text" value={q.id} readOnly className="w-full px-3 py-2 rounded text-sm opacity-60" style={inputStyle} />
      </div>

      {/* Topic + Difficulty row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: t.textSecondary }}>Topic</label>
          <select value={q.topic} onChange={(e) => update('topic', e.target.value as QuestionTopic)} className="w-full px-3 py-2 rounded text-sm" style={inputStyle}>
            {QUESTION_TOPICS.map((tp) => (
              <option key={tp.id} value={tp.id}>{tp.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: t.textSecondary }}>Difficulty</label>
          <select value={q.difficulty} onChange={(e) => update('difficulty', e.target.value as QuestionDifficulty)} className="w-full px-3 py-2 rounded text-sm" style={inputStyle}>
            {DIFFICULTIES.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Question text */}
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: t.textSecondary }}>Question</label>
        <textarea
          value={q.question}
          onChange={(e) => update('question', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 rounded text-sm resize-y"
          style={inputStyle}
        />
      </div>

      {/* Choices */}
      <div className="space-y-3">
        <label className="block text-xs font-medium" style={{ color: t.textSecondary }}>Choices</label>
        {q.choices.map((c) => (
          <div key={c.id} className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => update('correctAnswer', c.id)}
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors"
              style={{
                background: q.correctAnswer === c.id ? t.accent : t.surfaceHover,
                color: q.correctAnswer === c.id ? '#fff' : t.textSecondary,
                border: `2px solid ${q.correctAnswer === c.id ? t.accent : t.border}`,
              }}
              title={q.correctAnswer === c.id ? 'Correct answer' : 'Click to mark as correct'}
            >
              {c.id.toUpperCase()}
            </button>
            <input
              type="text"
              value={c.text}
              onChange={(e) => updateChoice(c.id, e.target.value)}
              placeholder={`Choice ${c.id.toUpperCase()}`}
              className="flex-1 px-3 py-2 rounded text-sm"
              style={inputStyle}
            />
          </div>
        ))}
        <p className="text-xs" style={{ color: t.textSecondary }}>
          Click a letter to mark it as the correct answer. Currently: <strong>{q.correctAnswer.toUpperCase()}</strong>
        </p>
      </div>

      {/* Explanation */}
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: t.textSecondary }}>Explanation</label>
        <textarea
          value={q.explanation}
          onChange={(e) => update('explanation', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 rounded text-sm resize-y"
          style={inputStyle}
        />
      </div>

      {/* Math toggle */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={q.isMath}
            onChange={(e) => update('isMath', e.target.checked)}
          />
          <span className="text-sm" style={{ color: t.textPrimary }}>Math / calculation question</span>
        </label>
      </div>

      {/* Linked qualifications */}
      <div>
        <label className="block text-xs font-medium mb-2" style={{ color: t.textSecondary }}>Linked Qualifications</label>
        <div className="flex flex-wrap gap-2">
          {QUAL_OPTIONS.map((qual) => {
            const active = q.linkedQualIds.includes(qual.id);
            return (
              <button
                key={qual.id}
                type="button"
                onClick={() => toggleQualId(qual.id)}
                className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
                style={{
                  background: active ? `${t.accent}30` : t.surfaceHover,
                  color: active ? t.accent : t.textSecondary,
                  border: `1px solid ${active ? t.accent : t.border}`,
                }}
              >
                {qual.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Save */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onSave}
          className="px-6 py-2.5 rounded font-medium transition-colors"
          style={{ background: t.accent, color: '#fff' }}
        >
          Save Question
        </button>
        <button
          onClick={onCancel}
          className="px-6 py-2.5 rounded font-medium transition-colors"
          style={{ background: t.surfaceHover, color: t.textSecondary }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
